import { ProductDetail } from './productDetailService';

interface CachedProduct {
  data: ProductDetail;
  timestamp: string;
  lastFetched: number;
}

class ProductCache {
  private cache = new Map<string, CachedProduct>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

  /**
   * Store product data in cache
   */
  set(productId: string, product: ProductDetail): void {
    this.cache.set(productId, {
      data: product,
      timestamp: product.updatedAt,
      lastFetched: Date.now(),
    });
  }

  /**
   * Get product data from cache if it exists and is still valid
   */
  get(productId: string): ProductDetail | null {
    const cached = this.cache.get(productId);
    
    if (!cached) {
      return null;
    }

    // Check if cache is still valid (within cache duration)
    const isExpired = Date.now() - cached.lastFetched > this.CACHE_DURATION;
    if (isExpired) {
      this.cache.delete(productId);
      return null;
    }

    return cached.data;
  }

  /**
   * Check if we have cached data and if it's still up to date
   * Returns the cached data if valid, null if needs refresh
   */
  getIfUpToDate(productId: string, currentUpdatedAt: string): ProductDetail | null {
    const cached = this.cache.get(productId);
    
    if (!cached) {
      return null;
    }

    // Check if the updated_at timestamp has changed
    if (cached.timestamp !== currentUpdatedAt) {
      this.cache.delete(productId);
      return null;
    }

    // Check if cache is still valid (within cache duration)
    const isExpired = Date.now() - cached.lastFetched > this.CACHE_DURATION;
    if (isExpired) {
      this.cache.delete(productId);
      return null;
    }

    return cached.data;
  }

  /**
   * Invalidate cache for a specific product (useful after updates)
   */
  invalidate(productId: string): void {
    this.cache.delete(productId);
  }

  /**
   * Clear all cached data
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }
}

// Export singleton instance
export const productCache = new ProductCache();
